/**
 * Organization Edit Page
 * Edit an existing organization
 */

import React from "react";
import { useParams, useNavigate, Navigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeftIcon } from "@heroicons/react/24/outline";
import Card from "../components/ui/Card";
import LoadingSpinner from "../components/common/LoadingSpinner";
import OrganizationForm from "../components/organizations/OrganizationForm";
import OrganizationPromoCodesTab from "../components/organizations/OrganizationPromoCodesTab";
import { getOrganization } from "../services/organizations.service";

const OrganizationEdit: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const {
    data: organization,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["organization", id],
    queryFn: () => getOrganization(id!),
    enabled: !!id,
  });

  const handleSuccess = () => {
    navigate(`/organizations/${id}`);
  };

  const handleCancel = () => {
    navigate(`/organizations/${id}`);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner size="lg" text="Loading organization..." />
      </div>
    );
  }

  if (error || !organization) {
    return (
      <div className="p-6">
        <Card
          variant="outlined"
          padding="md"
          className="border-red-200 bg-red-50"
        >
          <h3 className="text-red-800 font-semibold">
            Error loading organization
          </h3>
          <p className="text-red-600 text-sm mt-1">
            {error instanceof Error ? error.message : "Organization not found"}
          </p>
        </Card>
      </div>
    );
  }

  // A disabled (archived) organization is not editable — redirect to its detail page (which shows the
  // disabled banner + Restore). UI guard supplementing the DB lifecycle guard; direct-URL navigation
  // must not render an operational edit form for a disabled org.
  if (organization.is_archived) {
    return <Navigate to={`/organizations/${id}`} replace />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-4">
        <button
          onClick={() => navigate(`/organizations/${id}`)}
          className="text-gray-600 hover:text-gray-900 transition-colors"
          aria-label="Back to organization"
        >
          <ArrowLeftIcon className="h-6 w-6" />
        </button>
        <div>
          <h1 className="text-3xl font-bold text-gray-900">
            Edit Organization
          </h1>
          <p className="text-gray-600 mt-1">{organization.name}</p>
        </div>
      </div>

      {/* Form Card */}
      <Card>
        <OrganizationForm
          organization={organization}
          onSuccess={handleSuccess}
          onCancel={handleCancel}
        />
      </Card>

      {/* Promo codes, managed here rather than only on a separate tab. The Promo Code field in
          the form above is the SELECTED code; additional codes are added below and become
          immediately available to this organization's CTAs. */}
      <Card>
        <OrganizationPromoCodesTab organizationId={organization.id} />
      </Card>
    </div>
  );
};

export default OrganizationEdit;
